import React from "react";
import { MessageCircle, Repeat2, Heart, Share } from "lucide-react";
import { cn } from "../../lib/utils";
import DOMPurify from "dompurify";

// Helper function to calculate time ago
const getTimeAgo = (dateString) => {
  const now = new Date();
  const date = new Date(dateString);
  const diffInSeconds = Math.floor((now - date) / 1000);

  if (diffInSeconds < 60) {
    return `${diffInSeconds}s`;
  } else if (diffInSeconds < 3600) {
    const minutes = Math.floor(diffInSeconds / 60);
    return `${minutes}m`;
  } else if (diffInSeconds < 86400) {
    const hours = Math.floor(diffInSeconds / 3600);
    return `${hours}h`;
  } else if (diffInSeconds < 604800) {
    const days = Math.floor(diffInSeconds / 86400);
    return `${days}d`;
  } else if (diffInSeconds < 2592000) {
    const weeks = Math.floor(diffInSeconds / 604800);
    return `${weeks}w`;
  } else if (diffInSeconds < 31536000) {
    const months = Math.floor(diffInSeconds / 2592000);
    return `${months}mo`;
  } else {
    const years = Math.floor(diffInSeconds / 31536000);
    return `${years}y`;
  }
};

// Helper function to remove URLs from tweet text
const removeUrlsFromText = (text) => {
  if (!text) return text;
  
  // Remove URLs including:
  // - t.co shortened links (https://t.co/...)
  // - Regular http/https URLs
  // - www. URLs
  const urlPatterns = [
    /https?:\/\/t\.co\/[\w]+/gi, // t.co shortened links
    /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)/gi, // Full URLs
    /www\.[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)/gi, // www URLs
  ];
  
  let cleanedText = text;
  
  // Remove each URL pattern
  urlPatterns.forEach(pattern => {
    cleanedText = cleanedText.replace(pattern, "");
  });
  
  // Clean up any extra spaces that might be left
  cleanedText = cleanedText.replace(/\s+/g, " ");
  
  // Remove spaces before punctuation that might be left after URL removal
  cleanedText = cleanedText.replace(/\s+([.,;:!?])/g, "$1");
  
  // Remove leading/trailing spaces
  return cleanedText.trim();
};

const VerifiedBadge = () => (
  <svg className="w-4 h-4 text-blue-500 fill-current ml-1" viewBox="0 0 24 24">
    <path d="M22.5 12.5c0-1.58-.875-2.95-2.148-3.6.154-.435.238-.905.238-1.4 0-2.21-1.71-3.998-3.818-3.998-.47 0-.92.084-1.336.25C14.818 2.415 13.51 1.5 12 1.5s-2.816.917-3.437 2.25c-.413-.165-.866-.25-1.336-.25-2.11 0-3.818 1.79-3.818 4 0 .495.083.965.238 1.4-1.272.65-2.147 2.018-2.147 3.6 0 1.495.782 2.798 1.942 3.486-.02.17-.032.34-.032.514 0 2.21 1.708 4 3.818 4 .47 0 .92-.086 1.335-.25.62 1.334 1.926 2.25 3.437 2.25 1.512 0 2.818-.916 3.437-2.25.415.163.865.248 1.336.248 2.11 0 3.818-1.79 3.818-4 0-.174-.012-.344-.033-.513 1.158-.687 1.943-1.99 1.943-3.484zm-6.616-3.334l-4.334 6.5c-.145.217-.382.334-.625.334-.143 0-.288-.04-.416-.126l-.115-.094-2.415-2.415c-.293-.293-.293-.768 0-1.06s.768-.294 1.06 0l1.77 1.767 3.825-5.74c.23-.345.696-.436 1.04-.207.346.23.44.696.21 1.04z" />
  </svg>
);

const CustomizableTweetPreview = ({
  tweetData,
  previewSettings,
  className,
}) => {
  if (!tweetData) {
    return (
      <div
        className={cn(
          "bg-black text-white p-4 sm:p-6 rounded-xl border border-zinc-800 shadow-2xl max-w-md w-full mx-auto font-sans overflow-hidden",
          className
        )}
      >
        <div className="text-center text-zinc-500 py-8">
          <p>Enter a tweet URL to see preview</p>
        </div>
      </div>
    );
  }

  const {
    theme,
    backgroundColor,
    cardBackgroundColor,
    textColor,
    accentColor,
    fontFamily,
    fontSize,
    showMetrics,
    timeDisplay = "datetime",
    timeAgoDisplay = "show",
    aspectRatio,
    backgroundType,
    wrapperBackgroundColor,
    gradientBackground,
    paddingType,
    customPadding,
    logoStyle,
  } = previewSettings || {};

  // Get the card's internal background (theme-based)
  const getCardBackground = () => {
    // If cardBackgroundColor is explicitly set, use it
    if (cardBackgroundColor) {
      return {
        backgroundColor: cardBackgroundColor,
      };
    }
    
    // Otherwise use theme-based or backgroundColor from Theme Panel
    const bgColor =
      theme === "light"
        ? "#ffffff"
        : theme === "dark"
        ? "#000000"
        : backgroundColor || "#000000";

    return {
      backgroundColor: bgColor,
    };
  };
  const txtColor =
    theme === "light"
      ? "#000000"
      : theme === "dark"
      ? "#ffffff"
      : textColor || "#ffffff";
  const accent = accentColor || "#1d9bf0";

  // Calculate relative font sizes based on base fontSize
  const baseFontSize = fontSize || 16;
  const authorNameSize = aspectRatio !== "auto" ? baseFontSize * 0.75 : baseFontSize * 0.875; // Smaller for author name
  const usernameSize = aspectRatio !== "auto" ? baseFontSize * 0.625 : baseFontSize * 0.75; // Smaller for username
  const tweetTextSize = aspectRatio !== "auto" ? baseFontSize * 0.75 : baseFontSize; // Base size for tweet text
  const dateSize = aspectRatio !== "auto" ? baseFontSize * 0.625 : baseFontSize * 0.75; // Smaller for date/views
  const metricsSize = aspectRatio !== "auto" ? baseFontSize * 0.625 : baseFontSize * 0.75; // Smaller for metrics

  const author = tweetData.author || {};
  const metrics = tweetData.metrics || {};

  // Calculate aspect ratio styles
  const getAspectRatioStyle = () => {
    if (aspectRatio === "auto") {
      return {
        maxWidth: "100%",
        width: "100%",
        height: "auto",
      };
    }

    const aspectRatios = {
      "1:1": "1 / 1",
      "3:4": "3 / 4",
      "4:5": "4 / 5",
      "9:16": "9 / 16",
      "16:9": "16 / 9",
    };

    return {
      aspectRatio: aspectRatios[aspectRatio] || "auto",
      width: "100%",
      maxWidth: "100%",
      height: "100%",
      maxHeight: "100%",
    };
  };

  // Debug logging
  console.log("[Tweet Preview] Received tweetData:", {
    hasAuthor: !!author,
    authorName: author.name,
    metrics: metrics,
    showMetrics: previewSettings?.showMetrics,
    aspectRatio: aspectRatio,
  });

  // Get wrapper background style (behind the card)
  const getWrapperBackgroundStyle = () => {
    // If background type is gradient, use gradient
    if (backgroundType === "gradient" && gradientBackground) {
      return {
        background: gradientBackground,
      };
    }

    // Otherwise use wrapperBackgroundColor from Background Panel (not Theme Panel)
    const bgColor = wrapperBackgroundColor || "#000000";

    return {
      backgroundColor: bgColor,
    };
  };

  // Get padding value based on padding type
  const getPaddingValue = () => {
    const paddingValues = {
      compact: 16,
      standard: 32,
      spacious: 64,
      custom: customPadding || 32,
    };
    return paddingValues[paddingType] || 32;
  };

  return (
    <div
      id="tweet-preview-export"
      className="w-full rounded-lg flex flex-col items-center relative"
      style={{
        ...getWrapperBackgroundStyle(),
        padding: `${getPaddingValue()}px`,
      }}
    >
      <div
        className={cn(
          "rounded-xl border border-zinc-800 shadow-2xl mx-auto font-sans overflow-hidden",
          aspectRatio === "auto" ? "max-w-md w-full" : "w-full",
          aspectRatio !== "auto" ? "relative" : "",
          className
        )}
        style={{
          ...getCardBackground(),
          color: txtColor,
          fontFamily:
            fontFamily === "Twitter" ? "system-ui, -apple-system" : fontFamily,
          fontSize: `${fontSize}px`,
          ...(aspectRatio === "auto" ? {} : getAspectRatioStyle()),
          boxSizing: 'border-box',
        }}
      >
        <div
          className={cn(
            aspectRatio === "auto" 
              ? "p-4 sm:p-6 overflow-hidden" 
              : "p-4 sm:p-6 h-full overflow-y-auto overflow-x-hidden",
            "min-w-0" // Critical: prevents flex/grid children from overflowing
          )}
          style={{
            padding:
              aspectRatio !== "auto" ? "clamp(12px, 2vw, 24px)" : undefined,
            boxSizing: 'border-box',
            width: '100%',
            minWidth: 0, // Critical: allows flex children to shrink below content size
            maxWidth: '100%', // Ensure it never exceeds parent
          }}
        >
          <div
            className={cn(
              aspectRatio !== "auto" && "h-full flex flex-col",
              "min-w-0" // Prevent flex children from overflowing
            )}
            style={{
              width: '100%',
              minWidth: 0,
            }}
          >
            <div
              className={cn(
                "flex items-start justify-between gap-2",
                aspectRatio !== "auto" && "flex-shrink-0"
              )}
            >
              <div className="flex gap-2 sm:gap-3 min-w-0 flex-1">
                {author.avatar ? (
                  <img
                    src={author.avatar}
                    alt={author.name}
                    className={cn(
                      "rounded-full flex-shrink-0",
                      aspectRatio !== "auto"
                        ? "w-6 h-6"
                        : "w-8 h-8 sm:w-10 sm:h-10"
                    )}
                  />
                ) : (
                  <div
                    className={cn(
                      "rounded-full flex items-center justify-center text-white font-bold flex-shrink-0",
                      aspectRatio !== "auto"
                        ? "w-6 h-6 text-xs"
                        : "w-8 h-8 sm:w-10 sm:h-10 text-xs sm:text-sm"
                    )}
                    style={{ backgroundColor: accent }}
                  >
                    {author.name?.[0]?.toUpperCase() || "U"}
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <div
                    className="flex items-center font-bold leading-tight break-words"
                    style={{ fontSize: `${authorNameSize}px` }}
                  >
                    <span className="truncate" style={{ color: txtColor }}>
                      {author.name || "Unknown"}
                    </span>
                    {author.verified && <VerifiedBadge />}
                  </div>
                  <div
                    className="truncate"
                    style={{ color: txtColor, opacity: 0.7, fontSize: `${usernameSize}px` }}
                  >
                    @{author.username || "unknown"}
                  </div>
                </div>
              </div>
              {logoStyle !== "none" && (
                <div
                  className="flex-shrink-0"
                  style={
                    logoStyle === "twitter"
                      ? { color: "#1d9bf0" } // Twitter blue
                      : { color: txtColor, opacity: 0.5 }
                  }
                >
                  {logoStyle === "twitter" ? (
                    // Twitter Bird Logo (Blue)
                    <svg
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                      className="h-4 w-4 sm:h-5 sm:w-5 fill-current"
                      style={{ color: "#1d9bf0" }}
                    >
                      <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z" />
                    </svg>
                  ) : logoStyle === "x" ? (
                    // X Logo (Same color as text)
                    <svg
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                      className="h-4 w-4 sm:h-5 sm:w-5 fill-current"
                    >
                      <path d="M13.3174 10.7749L19.1457 4H17.7646L12.7039 10.1375L8.66193 4H4L10.1122 12.8958L4 20.7393H5.38119L10.7254 14.3578L14.952 20.7393H19.6139L13.3171 10.7749H13.3174ZM11.4257 13.3364L10.8064 12.4721L5.87886 4.72784H8.00029L12.1717 10.8689L12.7909 11.7332L17.7652 19.9775H15.6438L11.4257 13.3364Z" />
                    </svg>
                  ) : (
                    // Default three dots menu
                    <svg
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                      className="h-4 w-4 sm:h-5 sm:w-5 fill-current"
                    >
                      <g>
                        <path d="M3 12c0-1.1.9-2 2-2s2 .9 2 2-.9 2-2 2-2-.9-2-2zm9 2c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm7 0c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2z"></path>
                      </g>
                    </svg>
                  )}
                </div>
              )}
            </div>

            <div
              className={cn(
                "mt-3 leading-normal break-words",
                aspectRatio !== "auto" && "flex-shrink-0"
              )}
              style={{ color: txtColor, opacity: 0.9, fontSize: `${tweetTextSize}px` }}
            >
              {tweetData.html ? (
                <div
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(tweetData.html, {
                      ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'a', 'p', 'br', 'span', 'div', 'u', 's', 'blockquote'],
                      ALLOWED_ATTR: ['href', 'style', 'class', 'dir', 'data-*'],
                      ALLOW_DATA_ATTR: true,
                      KEEP_CONTENT: true,
                      // Allow CSS properties that Twitter uses for text styling
                      ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|sms|cid|xmpp|data):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i,
                      // Preserve CSS properties including writing-mode, text-orientation, direction, etc.
                      ALLOW_UNKNOWN_PROTOCOLS: false,
                      // Use a custom hook to preserve all CSS properties
                      FORBID_TAGS: [],
                      FORBID_ATTR: [],
                    })
                  }}
                  className="tweet-content"
                  style={{
                    wordBreak: 'break-word',
                    whiteSpace: 'pre-wrap',
                    // Don't override inline styles from Twitter
                  }}
                />
              ) : (
                removeUrlsFromText(tweetData.text) || "No tweet text available"
              )}
            </div>

            {/* Media - Only show first image */}
            {tweetData.media && tweetData.media.length > 0 && tweetData.media[0] && tweetData.media[0].url && (
              <div
                className={cn(
                  "mt-3 rounded-xl overflow-hidden",
                  aspectRatio !== "auto" && "flex-1 min-h-0 flex items-center justify-center",
                  "min-w-0" // Critical: prevents flex child from overflowing
                )}
                style={{
                  width: '100%',
                  maxWidth: '100%',
                  minWidth: 0, // Critical: prevents overflow in flex containers
                  maxHeight: aspectRatio !== "auto" ? '100%' : 'none',
                  boxSizing: 'border-box',
                  position: 'relative',
                  contain: 'layout style paint', // CSS containment for better performance and containment
                }}
              >
                <img
                  src={tweetData.media[0].url}
                  alt={tweetData.media[0].alt || ""}
                  className="rounded-xl"
                  style={{
                    // Use explicit constraints to prevent overflow
                    width: '100%',
                    maxWidth: '100%',
                    minWidth: 0,
                    height: aspectRatio !== "auto" ? '100%' : 'auto',
                    maxHeight: aspectRatio !== "auto" ? '100%' : 'none',
                    minHeight: 0,
                    objectFit: 'contain',
                    objectPosition: 'center',
                    display: 'block',
                    boxSizing: 'border-box',
                    // Prevent image from breaking out
                    verticalAlign: 'middle',
                    // Ensure image respects container bounds
                    contain: 'intrinsic-size',
                  }}
                  onError={(e) => {
                    console.error("[Tweet Preview] Failed to load image:", tweetData.media[0].url);
                    e.target.style.display = 'none';
                  }}
                  onLoad={(e) => {
                    // Ensure image doesn't exceed container after load
                    const img = e.target;
                    const container = img.parentElement;
                    if (container && img.naturalWidth > 0) {
                      // Force recalculation of dimensions
                      img.style.maxWidth = '100%';
                      img.style.maxHeight = container.clientHeight + 'px';
                    }
                  }}
                />
              </div>
            )}

            {tweetData.createdAt && timeDisplay !== "hidden" && (
              <div
                className="mt-3 hover:underline cursor-pointer break-words flex items-center gap-2 flex-shrink-0"
                style={{ color: txtColor, opacity: 0.5, fontSize: `${dateSize}px` }}
              >
                {timeDisplay === "datetime" ? (
                  <>
                    <span>
                      {new Date(tweetData.createdAt).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })}
                    </span>
                    <span>·</span>
                    <span>
                      {new Date(tweetData.createdAt).toLocaleTimeString("en-US", {
                        hour: "numeric",
                        minute: "2-digit",
                        hour12: true,
                      })}
                    </span>
                    {timeAgoDisplay === "show" && (
                      <>
                        <span>·</span>
                        <span>
                          {getTimeAgo(tweetData.createdAt)}
                        </span>
                      </>
                    )}
                  </>
                ) : (
                  <>
                    <span>
                      {new Date(tweetData.createdAt).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })}
                    </span>
                    {timeAgoDisplay === "show" && (
                      <>
                        <span>·</span>
                        <span>
                          {getTimeAgo(tweetData.createdAt)}
                        </span>
                      </>
                    )}
                  </>
                )}
              </div>
            )}

            {showMetrics && (
              <>
                <div
                  className="h-[1px] my-3 flex-shrink-0"
                  style={{ backgroundColor: txtColor, opacity: 0.2 }}
                ></div>

                <div
                  className={cn(
                    "flex justify-between px-1 sm:px-2 flex-wrap gap-2 flex-shrink-0",
                    aspectRatio !== "auto" ? "gap-2" : "gap-4 sm:gap-6"
                  )}
                  style={{ color: txtColor, opacity: 0.7 }}
                >
                  <div className="flex items-center gap-1 sm:gap-2 group cursor-pointer hover:opacity-100 transition-opacity">
                    <MessageCircle
                      className={
                        aspectRatio !== "auto"
                          ? "w-3 h-3"
                          : "w-4 h-4 sm:w-[18px] sm:h-[18px]"
                      }
                    />
                    <span style={{ fontSize: `${metricsSize}px` }}>
                      {metrics.replies || 0}
                    </span>
                  </div>
                  <div className="flex items-center gap-1 sm:gap-2 group cursor-pointer hover:opacity-100 transition-opacity">
                    <Repeat2
                      className={
                        aspectRatio !== "auto"
                          ? "w-3 h-3"
                          : "w-4 h-4 sm:w-[18px] sm:h-[18px]"
                      }
                    />
                    <span style={{ fontSize: `${metricsSize}px` }}>
                      {metrics.retweets || 0}
                    </span>
                  </div>
                  <div className="flex items-center gap-1 sm:gap-2 group cursor-pointer hover:opacity-100 transition-opacity">
                    <Heart
                      className={
                        aspectRatio !== "auto"
                          ? "w-3 h-3"
                          : "w-4 h-4 sm:w-[18px] sm:h-[18px]"
                      }
                    />
                    <span style={{ fontSize: `${metricsSize}px` }}>
                      {metrics.likes || 0}
                    </span>
                  </div>
                  <div className="flex items-center gap-1 sm:gap-2 group cursor-pointer hover:opacity-100 transition-opacity">
                    <Share
                      className={
                        aspectRatio !== "auto"
                          ? "w-3 h-3"
                          : "w-4 h-4 sm:w-[18px] sm:h-[18px]"
                      }
                    />
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Watermark - Below the card */}
      <div className="w-full mt-4 flex justify-center">
        <div
          className={cn(
            "text-center py-2 px-4 rounded",
            aspectRatio === "auto" ? "max-w-md w-full" : "w-full"
          )}
          style={{
            backgroundColor: accent,
            color: '#ffffff',
            fontSize: `${Math.max(10, baseFontSize * 0.625)}px`,
            fontWeight: 500,
          }}
        >
          Made with SnappyTweet
        </div>
      </div>
    </div>
  );
};

export default CustomizableTweetPreview;
